/**
 * The launcher for a grammar course.
 *
 * Not the generic CEFR-chip layout, because these decks have no levels to
 * filter — they have a sequence, and the only question the home has to answer
 * is "what is today's lesson".
 *
 * The progress strip is the thing a finite curriculum can show that an infinite
 * one cannot: every lesson as a dot, in named blocks, filling up, ending. It is
 * the whole argument for building a curriculum instead of a treadmill, made
 * visible.
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, ChartNoAxesColumn } from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { api } from '@/lib/api';

import { GrammarProgress, type GrammarState } from './Progress';
import { readingOrder, type GrammarCourse } from './types';

type Props = {
  appId: string;
  course: GrammarCourse;
  /** The course's own name, in its own language. */
  displayName: string;
  tagline: string;
  primaryLang?: string;
};

export function GrammarAppHome({ appId, course, displayName, tagline, primaryLang }: Props) {
  const navigate = useNavigate();
  const stateQuery = useQuery<GrammarState>({
    queryKey: [appId, 'state'],
    queryFn: () => api.grammarState<GrammarState>(appId),
  });

  const state = stateQuery.data;

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 py-6">
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 self-start text-sm"
      >
        <ArrowLeft className="size-4" aria-hidden />
        All apps
      </Link>

      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold">{displayName}</h1>
        <p className="text-muted-foreground">{tagline}</p>
      </header>

      {stateQuery.isPending && <p className="text-muted-foreground">Loading…</p>}

      {stateQuery.isError && (
        <p className="text-destructive">
          Could not load your progress. Check the connection and try again.
        </p>
      )}

      {state && (
        <>
          <TodayCard
            state={state}
            course={course}
            primaryLang={primaryLang}
            onStart={() => navigate(`/apps/${appId}/practice`)}
          />
          {/* The way in to everything the progress strip cannot say: per-lesson
              accuracy, the lessons you saved, and re-drilling one on demand.
              A link rather than a fourth thing on this screen — the home
              answers "what is today", and this answers "how is it going". */}
          <Link
            to={`/apps/${appId}/review`}
            className="border-border hover:bg-muted/60 flex min-h-12 items-center gap-2.5 rounded-lg border px-4 text-sm font-medium"
          >
            <ChartNoAxesColumn className="text-muted-foreground size-4" aria-hidden />
            How it is going
            <span className="text-muted-foreground ml-auto text-xs font-normal">
              scores · saved · practise again
            </span>
          </Link>

          <GrammarProgress state={state} appId={appId} course={course} primaryLang={primaryLang} />
        </>
      )}
    </main>
  );
}

function TodayCard({
  state,
  course,
  primaryLang,
  onStart,
}: {
  state: GrammarState;
  course: GrammarCourse;
  primaryLang?: string;
  onStart: () => void;
}) {
  const next = state.next_lesson;
  const isReview = next === null;
  // Both explanation languages, in her reading order. The old version printed
  // the English title under the German one regardless of who was looking.
  const titles = next ? readingOrder(next.title, course, primaryLang) : [];

  if (state.done_today) {
    return (
      <section className="border-success/30 bg-success/5 flex flex-col items-center gap-3 rounded-2xl border p-8 text-center">
        <Check className="text-success size-8" aria-hidden />
        <p className="font-display text-xl">Done for today</p>
        <p className="text-muted-foreground text-sm">
          {isReview
            ? 'The curriculum is finished. Come back tomorrow for another review.'
            : `Lesson ${next.n} is waiting for tomorrow.`}
        </p>
        <button
          type="button"
          onClick={onStart}
          className="text-muted-foreground text-sm underline-offset-4 hover:underline"
        >
          Do another anyway
        </button>
      </section>
    );
  }

  return (
    <section className="border-border bg-card flex flex-col gap-4 rounded-2xl border p-6 shadow-sm sm:p-8">
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          {isReview ? 'Review lesson' : `Today · lesson ${next.n}`}
        </span>
        <p className="font-display text-2xl font-semibold">
          {isReview ? 'Everything you have learned' : (titles[0] ?? `Lesson ${next.n}`)}
        </p>
        {!isReview && titles[1] && <p className="text-muted-foreground">{titles[1]}</p>}
      </div>
      <button
        type="button"
        onClick={onStart}
        className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        Start
      </button>
    </section>
  );
}
